/**
 * Diary entry formats — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_writing_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const diaryEntryFormatsPack: ToilePack = {
    slug: 'diary-entry-formats',
    category: 'classroom',
    name: 'Diary entry formats',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'trip', label: 'School trip', subject: 'a diary page with "Day", "Date", "Time", "Dear Diary", "Body", "Name" labelled'},
        {key: 'exam', label: 'Exam day', subject: 'a diary entry format on a notebook page with the parts boxed and a doodle in the corner'},
        {key: 'festival', label: 'Festival day', subject: 'a diary entry format with a small diya doodle and labelled parts'},
    ],
    styles: [
        {key: 'notebook', label: 'Notebook page', style: 'school exercise notebook page, neat hand-lettered pen labels on ruled lines, red margin line', palette: 'blue ruled lines, black pen labels, red margin line', framing: 'flat top-down page filling the frame, straight on'},
        {key: 'colorcoded', label: 'Colour-coded', style: 'clean printable teaching worksheet, bold rounded headings in boxes, each part of the format in its own colour', palette: 'navy, teal and orange labels and boxes on white paper', framing: 'flat page centered with an even white margin'},
        {key: 'mono', label: 'Black and white', style: 'photocopy-ready black-and-white worksheet, thin crisp outlines, bracket and arrow callouts naming each part', palette: 'black ink only', framing: 'flat page centered with an even white margin'},
    ],
    directions: ['red pen marks', 'filled sample', 'bigger labels'],
    directionHint: 'try: red pen marks',
    seo: {
        title: 'Diary Entry Format: Trip, Exam Day, Festival',
        metaDescription: 'Diary entry format on one labelled page: a diary entry about a school trip, an exam day and a festival, in three styles.',
        h1: 'Diary entry format: a school trip, an exam day, a festival',
        tagline: 'Nine labelled pages, three diary entries in notebook, colour-coded and black and white styles.',
        intro: 'A printable worksheet showing the layout of a diary entry, with each part labelled: day, date, time, the greeting to the diary, body and name. The nine pages show entries about a school trip, an exam day and a festival, the festival page with a small diya lamp doodle. Ask for another day or occasion and the same labelled layout is asked for.',
        useCases: [
            'a classroom bulletin board',
            'a writing notebook reference page',
            'a handout for a diary writing lesson',
            "a sheet for a student's first diary entry",
        ],
        faq: [
            {q: 'What is labelled on a diary page?', a: 'The day, date, time, the greeting to the diary, the body and the name.'},
            {q: 'Does the page show a finished entry?', a: 'No. The body is drawn as ruled lines, so the page shows the layout, not the wording.'},
            {q: 'How do the three styles differ?', a: 'Notebook page is hand-lettered pen labels on ruled lines with a red margin, like a school exercise book. Colour-coded gives each part its own colour in a box. Black and white is thin outlines in ink only, ready to photocopy.'},
            {q: 'Can I ask for an entry about another day?', a: 'Yes. Add a direction for another day or occasion and the labelled layout is asked for with it.'},
        ],
    },
}
